// Copyright © 2023 Move Closer
import { __decorate } from "tslib";
import { Component, Prop, Vue } from 'vue-property-decorator';
import { PlayerState } from '../../../_contracts';
import { YouTubeIframeLoader } from '../../../_helpers/youtube-iframe-loader';
import { resolveVideoIdFromUrl } from '../../Tile';
let VideoPlayerYouTube = class VideoPlayerYouTube extends Vue {
    constructor() {
        super(...arguments);
        this.isVideoPlaying = false;
        this.player = null;
        this.eventHandlers = [];
    }
    get videoMetadata() {
        return {
            ...this.baseVideoMetadata,
            embedUrl: `https://www.youtube.com/embed/${this.videoId}`
        };
    }
    onPlay(event) {
        if (typeof event.payload === 'undefined') {
            return;
        }
        const { playerId, autoplay, muted } = event.payload;
        if (playerId !== this.videoAttrId) {
            return;
        }
        this.initPlayer(autoplay, muted);
    }
    onPause(event) {
        if (typeof event.payload === 'undefined') {
            return;
        }
        const { playerId } = event.payload;
        if (playerId !== this.videoAttrId) {
            return;
        }
        if (this.player) {
            this.player.pauseVideo();
        }
    }
    onStateChanged(event) {
        if (this.player === null || typeof event.payload === 'undefined') {
            return;
        }
        const { playerId, state } = event.payload;
        if (playerId === this.videoAttrId) {
            return;
        }
        if (state === PlayerState.Playing) {
            this.player.pauseVideo();
        }
    }
    watchEvents() {
        if (!this.eventBus) {
            return;
        }
        this.eventHandlers = [
            this.eventBus.handle('ui:video-player.play', this.onPlay),
            this.eventBus.handle('ui:video-player.pause', this.onPause),
            this.eventBus.handle('ui:video-player.state-changed', this.onStateChanged)
        ];
    }
    clearEvents() {
        this.eventHandlers.forEach(handler => handler.unsubscribe());
    }
    /**
     * Initialises the YouTube player.
     *
     * @param autoplay - Determines whether the video should start playing
     *   immediately after the player has been initialised.
     * @param muted - Determine whether the video should be started with volume 0.
     */
    initPlayer(autoplay = false, muted = false) {
        this.$emit('init');
        // @ts-expect-error
        YouTubeIframeLoader.load(YT => {
            this.player = new YT.Player(this.videoAttrId, {
                events: {
                    onReady: ({ target }) => {
                        this.player = target;
                        if (!autoplay || !this.player) {
                            return;
                        }
                        if (muted) {
                            target.mute();
                        }
                        // prevent "Failed to execute 'postMessage' on 'DOMWindow'" error
                        // https://stackoverflow.com/a/56607403/3305944
                        setTimeout(() => target.playVideo(), 500);
                    },
                    onStateChange: (event) => {
                        const { data: state } = event;
                        if (typeof state !== 'number') {
                            console.warn(`YT.Player.onStateChange(): Expected [event.data] to be a type of [number], but got [${typeof state}]!`);
                            return;
                        }
                        if (this.player === null) {
                            console.warn('YT.Player.onStateChange(): FATAL! [this.player] is [null]!');
                            return;
                        }
                        this.isVideoPlaying = state === PlayerState.Playing;
                        const payload = {
                            playerId: this.videoAttrId,
                            state
                        };
                        if (this.eventBus) {
                            this.eventBus.emit('ui:video-player.state-changed', payload);
                        }
                        if (this.isVideoPlaying) {
                            this.$emit('play');
                        }
                        else {
                            this.$emit('pause');
                        }
                    }
                },
                height: '100%',
                width: '100%',
                videoId: this.videoId,
                enablejsapi: 1,
                playsinline: 0,
                origin: this.origin
            });
        });
    }
    get videoId() {
        return resolveVideoIdFromUrl(this.videoSrc);
    }
};
__decorate([
    Prop({ type: Object, required: true })
], VideoPlayerYouTube.prototype, "baseVideoMetadata", void 0);
__decorate([
    Prop({ type: Object, required: false })
], VideoPlayerYouTube.prototype, "eventBus", void 0);
__decorate([
    Prop({ type: String, required: true })
], VideoPlayerYouTube.prototype, "videoAttrId", void 0);
__decorate([
    Prop({ type: String, required: false })
], VideoPlayerYouTube.prototype, "videoAdsTag", void 0);
__decorate([
    Prop({ type: String, required: true })
], VideoPlayerYouTube.prototype, "videoSrc", void 0);
__decorate([
    Prop({ type: String, required: false })
], VideoPlayerYouTube.prototype, "videoClass", void 0);
__decorate([
    Prop({ type: String, required: true })
], VideoPlayerYouTube.prototype, "origin", void 0);
__decorate([
    Prop({ type: Boolean, required: false, default: false })
], VideoPlayerYouTube.prototype, "shouldInit", void 0);
VideoPlayerYouTube = __decorate([
    Component({
        name: 'VideoPlayerYouTube',
        mounted() {
            this.$nextTick(() => {
                if (this.shouldInit) {
                    this.initPlayer();
                }
            });
            this.watchEvents();
        },
        beforeDestroy() {
            if (this.player && this.player.destroy) {
                this.player.destroy();
            }
            this.player = null;
            this.clearEvents();
        },
        template: `
    <div>
      <component :is="'script'" type="application/ld+json" v-html="JSON.stringify(videoMetadata)" />

      <div :id="videoAttrId" :class="videoClass" ref="video" />
    </div>
  `
    })
], VideoPlayerYouTube);
export { VideoPlayerYouTube };
export default VideoPlayerYouTube;
